import { Container, Sprite, type Texture } from "pixi.js";
import { FrameSequencePlayer } from "../../view/FrameSequencePlayer";
import type { PresentationTicker } from "../../runner/PresentationTicker";

/**
 * Frame-sequence burst for symbol removal VFX — `SymbolRemovalBurst.prefab` (a 500×500
 * sprite cycling the `Explosion_Sheet` frames at 24 fps). Ticked by the presentation ticker's raw
 * delta: wall-clock paced, but pause/frame-step aware.
 */
export class SymbolRemovalBurst extends Container {
  public readonly sprite: Sprite;
  private readonly player: FrameSequencePlayer;
  private readonly ticker: PresentationTicker;
  private unsubscribe: (() => void) | null = null;

  public constructor(input: { frames: Texture[]; fps?: number; ticker: PresentationTicker; scale?: number }) {
    super({ label: "SymbolRemovalEffect" });
    this.ticker = input.ticker;
    this.sprite = new Sprite({ texture: input.frames[0], anchor: 0.5 });
    if (input.scale !== undefined) this.sprite.scale.set(input.scale);
    this.addChild(this.sprite);
    this.player = new FrameSequencePlayer({ frames: input.frames, fps: input.fps ?? 24, sprite: this.sprite, loop: false });
  }

  public play(onComplete?: () => void): void {
    this.stop();
    this.visible = true;
    this.unsubscribe = this.ticker.add((dt) => this.player.update(dt));
    this.player.play(() => {
      this.stop();
      onComplete?.();
    });
  }

  public stop(): void {
    this.player.stop();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  public override destroy(options?: Parameters<Container["destroy"]>[0]): void {
    this.stop();
    super.destroy(options);
  }
}
